<template>
    <PageLoading v-if="isLoading" />
    <div v-else class="main-wrap courses-category-page">
        <h1>{{ $t('general.courses_catalog') }}</h1>
        <div class="filter-wrap row q-col-gutter-md">
            <q-select
                v-model="areaSelected"
                class="col-12 col-sm-6 col-md-4 col-lg-3"
                square
                outlined
                dense
                clearable
                :options="filteredAreas"
                option-value="id"
                :option-label="(option) => JSON.parse(option.title)[locale]"
                map-options
                use-input
                fill-input
                hide-selected
                emit-value
                :label="$tc('general.training_areas', 1)"
                @filter="filterAreas"
                @input="resetPagination"
            >
            </q-select>
            <q-select
                v-model="partnerSelected"
                class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-auto"
                square
                outlined
                dense
                clearable
                :options="partners"
                option-value="id"
                :option-label="(option) => JSON.parse(option.title)[locale]"
                map-options
                use-input
                fill-input
                hide-selected
                emit-value
                :label="$tc('general.partners', 1)"
                @filter="filterPartners"
                @input="resetPagination"
            ></q-select>
            <q-select
                v-model="promotionSelected"
                class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-grow"
                square
                outlined
                dense
                clearable
                :options="promotions"
                :label="$t('general.promotion')"
                option-value="value"
                option-label="label"
                emit-value
                map-options
                @input="resetPagination"
            />
            <q-select
                v-model="typeSelected"
                class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-grow"
                square
                outlined
                dense
                clearable
                :options="types"
                :label="$t('general.type')"
                option-value="id"
                :option-label="(option) => JSON.parse(option.title)[locale]"
                emit-value
                map-options
                @input="resetPagination"
            />
            <q-select
                v-model="localeId"
                class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-grow"
                square
                outlined
                dense
                clearable
                :options="courseLocales"
                :label="$t('general.course_locales')"
                option-value="id"
                :option-label="
                    (option) => JSON.parse(option.description)[locale]
                "
                emit-value
                map-options
                @input="resetPagination"
            />
            <q-select
                v-model="sortBySelected"
                class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-grow"
                square
                outlined
                dense
                clearable
                :options="sortBy"
                :label="$t('general.order_by')"
                @input="resetPagination"
            />
            <q-select
                v-model="durationSelected"
                class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-grow"
                square
                outlined
                dense
                clearable
                :options="duration"
                :label="$t('general.duration')"
                @input="resetPagination"
            />
            <q-input
                v-model="search"
                class="col-12 col-sm-6 col-md-grow"
                :placeholder="$t('general.search_courses')"
                square
                outlined
                dense
                clearable
                debounce="1000"
                :input-style="{ color: 'grey' }"
                @input="resetPagination"
            >
            </q-input>
            <div class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-shrink">
                <q-btn
                    class="search-btn"
                    icon="search"
                    color="primary"
                    unelevated
                    dense
                    @click="fetchCourses()"
                ></q-btn>
            </div>
        </div>
        <div v-if="courses.length" class="courses-wrap">
            <CourseThumb
                v-for="(course, index) in courses"
                :key="index"
                :index="index"
                :course="course"
            />
        </div>
        <h5 v-else>{{ $t('general.no_results') }}</h5>
        <div class="pagination-wrap">
            <q-pagination
                v-if="pagination.max != 0"
                v-model="pagination.page"
                text-color="grey-8"
                :max="pagination.max"
                :max-pages="6"
                :direction-links="true"
                :boundary-links="true"
                :boundary-numbers="true"
                @input="fetchCourses"
            ></q-pagination>
        </div>
    </div>
</template>

<script>
import PageLoading from '@/components/PageLoading'
import CourseThumb from '@/components/CourseThumb'
import coursesService from '@/services/modules/courses'
import courseLocalesService from '@/services/modules/courselocales'
import areasService from '@/services/modules/areas'
import partnersService from '@/services/modules/partners'
import courseTypesService from '@/services/modules/courseTypes'

const loadingKeys = {
    fetchingCourses: 'fetching-courses',
    fetchingAreas: 'fetching-areas',
    fetchingPartners: 'fetching-partners',
    fetchingTypes: 'fetching-types',
    fetchingCourseLocales: 'fetching-course-locales',
}

export default {
    name: 'Courses',

    components: {
        PageLoading,
        CourseThumb,
    },

    data() {
        return {
            search: '',
            areaSelected: null,
            partnerSelected: null,
            durationSelected: null,
            areas: [],
            filteredAreas: [],
            filteredPartners: [],
            partners: [],

            courseLocales: [],

            promotionSelected: null,
            promotions: [
                {
                    value: true,
                    label: this.$t('general.with_promotion'),
                },
                {
                    value: false,
                    label: this.$t('general.without_promotion'),
                },
            ],

            typeSelected: null,
            localeId: null,
            types: [],

            sortBySelected: null,
            sortBy: [
                {
                    label: this.$t('general.more_recents'),
                    column: 'created_at',
                    direction: 'desc',
                },
                {
                    label: this.$t('general.course_name_asc'),
                    column: 'title',
                    direction: 'asc',
                },
                {
                    label: this.$t('general.course_name_desc'),
                    column: 'title',
                    direction: 'desc',
                },
                {
                    label: this.$t('general.discount_bigger_smaller'),
                    column: 'promotion_discount',
                    direction: 'desc',
                },
                {
                    label: this.$t('general.discount_smaller_bigger'),
                    column: 'promotion_discount',
                    direction: 'asc',
                },
            ],

            duration: [
                {
                    label: this.$tc('general.duration_course_interval', 1, {
                        min: 0,
                        max: 20,
                    }),
                    min: 0,
                    max: 20,
                },
                {
                    label: this.$tc('general.duration_course_interval', 1, {
                        min: 20,
                        max: 40,
                    }),
                    min: 20,
                    max: 40,
                },
                {
                    label: this.$tc('general.duration_course_interval', 1, {
                        min: 40,
                        max: 60,
                    }),
                    min: 40,
                    max: 60,
                },
                {
                    label: this.$tc('general.duration_course_interval', 2, {
                        min: 60,
                    }),
                    min: 60,
                    max: null,
                },
            ],

            courses: [],

            pagination: {
                max: 1,
                page: 1,
                rowsPerPage: 10,
                rowsNumber: 0,
            },
        }
    },

    computed: {
        isLoading() {
            return (
                this.isFetchingCourses ||
                this.isFetchingAreas ||
                this.isFetchingPartners ||
                this.isFetchingCourseLocales
            )
        },

        isFetchingCourses() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingCourses
            ).length
        },

        isFetchingCourseLocaless() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingCourseLocales
            ).length
        },

        isFetchingAreas() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingAreas
            ).length
        },

        isFetchingPartners() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingPartners
            ).length
        },

        locale() {
            return this.$i18n.locale
        },

        searchQuery() {
            return this.$route.query.search
        },
    },

    watch: {
        search(value) {
            this.search = value
            this.fetchCourses()
        },
        $route(value) {
            this.search = value.query.search
        },
    },

    // beforeRouteLeave() {
    //     this.$emit('clear-search')
    //     next()
    // },
    beforeDestroy() {
        this.$emit('clear-search')
    },

    mounted() {
        this.search = this.searchQuery ? this.searchQuery : this.search
    },

    async created() {
        this.fetchTypes()
        this.fetchCourseLocales()
        this.fetchAreas()
        this.fetchPartners()
        this.initFilters()
        this.fetchCourses()
    },

    methods: {
        resetPagination() {
            this.pagination = {
                max: 1,
                page: 1,
                rowsPerPage: 10,
                rowsNumber: 0,
            }
            this.fetchCourses()
        },

        filterAreas(val, update) {
            update(() => {
                const needle = val.toLowerCase()
                this.filteredAreas = this.areas.filter(
                    (v) => v.title.toLowerCase().indexOf(needle) > -1
                )
            })
        },

        filterPartners(val, update) {
            update(() => {
                const needle = val.toLowerCase()
                this.filteredPartners = this.partners.filter(
                    (v) => v.title.toLowerCase().indexOf(needle) > -1
                )
            })
        },

        async initFilters() {
            if (this.$route.params) {
                this.areaSelected = parseInt(this.$route.params.areaId)
                    ? parseInt(this.$route.params.areaId)
                    : null
                this.partnerSelected = parseInt(this.$route.params.partnerId)
                    ? parseInt(this.$route.params.partnerId)
                    : null
            }
        },

        constructOrderBy() {
            let orderBy = this.sortBySelected
                ? {
                      column: this.sortBySelected.column,
                      direction: this.sortBySelected.direction,
                  }
                : { column: 'title', direction: 'asc' }
            return orderBy
        },

        constructPagination() {
            return {
                perPage: this.pagination.rowsPerPage,
                currentPage: this.pagination.page,
            }
        },

        async fetchCourses() {
            this.loadingAdd(loadingKeys.fetchingCourses)

            let parameters = {
                filter: {
                    area: this.areaSelected,
                    partner: this.partnerSelected,
                    onPromotion: this.promotionSelected,
                    coursetype: this.typeSelected,
                    search: this.search,
                    duration: this.durationSelected,
                    localeId: this.localeId,
                    published: true,
                    active: true,
                },
                orderBy: [this.constructOrderBy()],
                pagination: this.constructPagination(),
                relations: {
                    courses_coursetype: true,
                    courses_packagetypes: true,
                },
            }

            let response = await coursesService.retrieve(parameters)

            if (response.status === 200) {
                this.courses = response.data.results

                let total = response.data.pagination.total
                let pages = total / this.pagination.rowsPerPage
                this.pagination.max = Math.ceil(pages)
            }

            this.loadingRemove(loadingKeys.fetchingCourses)
        },

        async fetchCourseLocales() {
            this.loadingAdd(loadingKeys.fetchingCourseLocales)

            let parameters = {
                filter: {},
            }

            let response = await courseLocalesService.retrieve(parameters)

            if (response.status === 200) {
                this.courseLocales = response.data.results
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingCourseLocales)
        },

        async fetchAreas() {
            this.loadingAdd(loadingKeys.fetchingAreas)

            let parameters = {
                filter: {
                    active: true,
                    published: true,
                },
                orderBy: [
                    {
                        column: 'title',
                        direction: 'asc',
                    },
                ],
            }

            let response = await areasService.retrieve(parameters)

            if (response.status === 200) {
                this.areas = response.data.results
                this.filteredAreas = response.data.results
            }

            this.loadingRemove(loadingKeys.fetchingAreas)
        },

        async fetchPartners() {
            this.loadingAdd(loadingKeys.fetchingPartners)

            let parameters = {
                filter: {
                    active: true,
                },
                orderBy: [
                    {
                        column: 'title',
                        direction: 'asc',
                    },
                ],
            }

            let response = await partnersService.partners.retrieve(parameters)

            if (response.status === 200) {
                this.partners = response.data.results
                this.filteredPartners = response.data.results
            }

            this.loadingRemove(loadingKeys.fetchingPartners)
        },

        async fetchTypes() {
            this.loadingAdd(loadingKeys.fetchingTypes)

            let parameters = {}

            let response = await courseTypesService.retrieve(parameters)

            if (response.status === 200) {
                this.types = response.data.results
            }

            this.loadingRemove(loadingKeys.fetchingTypes)
        },
    },
}
</script>
